import type { Worksheet } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import type { DepartmentLocation, HospitalData, NurseInShift } from "../../../utils/models";
import type { CustomColumnNamesConfig, DailyPrintoutGrouping, LayoutShape, PrintoutColumn, PrintoutColumnType, RowNoteFlags, SectionPlan } from "../types";
import { renderPivotSectionHeader } from "./pivotSectionHeader";
import { renderPivotSectionRows } from "./pivotSectionRows";
import { calculateJobGroupStats, calculateRoleGroupStats, emitRoleGroupStatRows } from "./roleGroupStats";
import { emitColumnHeaders, emitSectionTitle } from "./sectionHeader";
import { renderStandardSectionRows } from "./standardSectionRows";
import { applyVerticalMerges } from "./verticalMerges";

interface RenderSectionArgs {
	worksheet: Worksheet;
	section: SectionPlan;
	currentRow: number;
	layout: LayoutShape;
	enabledColumns: PrintoutColumn[];
	rowGroupingBy: PrintoutColumnType[];
	printoutGroupBy: DailyPrintoutGrouping;
	hospitalData: HospitalData;
	department: Department;
	timezone: string;
	jobManagementEnabled: boolean;
	printNotes: boolean;
	staffPerRow: number;
	showRoleGroupStats: boolean;
	departmentLocationsRequired: DepartmentLocation[];
	censusByLocationName: Map<string, number>;
	dayAbbr: string;
	noteFlags: RowNoteFlags | undefined;
	sortBy: PrintoutColumn[] | undefined;
	customStringOrderMap: Map<string, number> | null | undefined;
	mergeSimilarShiftTimes: boolean | undefined;
	columnGroupingBy: PrintoutColumnType | null;
	appendRoleToName: boolean;
	customColumnNames: CustomColumnNamesConfig;
}

/**
 * Render one section into the worksheet (mapping §4 phase 6 / §6.* / §8.* / §9.2).
 *
 * Dispatches by `layout.kind`:
 * - `standard` → section title → optional role-group stats → column headers → packed data rows
 *   → vertical merges for `rowGroupingBy` columns
 * - `pivot` → 3-row pivot section header (title + bands + sub-column names) → pivoted data rows
 *
 * Returns the row after the section.
 */
export const renderSection = (args: RenderSectionArgs): number => {
	const {
		worksheet,
		section,
		currentRow: initialRow,
		layout,
		enabledColumns,
		rowGroupingBy,
		printoutGroupBy,
		hospitalData,
		department,
		timezone,
		jobManagementEnabled,
		printNotes,
		staffPerRow,
		showRoleGroupStats,
		departmentLocationsRequired,
		noteFlags,
		sortBy,
		customStringOrderMap,
		mergeSimilarShiftTimes,
		columnGroupingBy,
		censusByLocationName,
		appendRoleToName,
		customColumnNames,
	} = args;

	let currentRow = initialRow;

	if (layout.kind === "pivot") {
		currentRow = renderPivotSectionHeader({
			worksheet,
			groupingKey: section.groupingKey,
			currentRow,
			grouping: printoutGroupBy,
			anchorColumns: layout.layout.anchorColumns,
			subColumns: layout.layout.subColumns,
			horizontalValues: layout.layout.horizontalValues,
			horizontalValueLabels: layout.layout.horizontalValueLabels,
			department,
			jobManagementEnabled,
			columnGroupingBy,
			censusByLocationName,
			customColumnNames,
		});
		if (!columnGroupingBy) return currentRow; // shouldn't happen — pivot requires columnGroupingBy
		currentRow = renderPivotSectionRows({
			worksheet,
			rows: section.rows,
			currentRow,
			columnGroupingBy,
			layout: layout.layout,
			enabledColumns,
			rowGroupingBy,
			hospitalData,
			department,
			timezone,
			jobManagementEnabled,
			sortBy,
			customStringOrderMap,
			mergeSimilarShiftTimes,
			noteFlags,
			appendRoleToName,
		});
		return currentRow;
	}

	// Standard layout
	const hasNotesInColumns = layout.expandedColumns.some((col) => col.type === "notes");
	const totalColumns = layout.expandedColumns.length + (printNotes && !hasNotesInColumns ? 1 : 0);

	currentRow = emitSectionTitle({
		worksheet,
		groupingKey: section.groupingKey,
		currentRow,
		grouping: printoutGroupBy,
		totalColumns,
		department,
		jobManagementEnabled,
		censusByLocationName,
	});

	// Role/job-group stats for this section: count ALL of the section's nurses (incl. those with no
	// location), and sum required over the section's config locations (each carrying its
	// census-resolved amountRequired). Mirrors the daily view's per-group stats.
	if (showRoleGroupStats) {
		const sectionNurses: NurseInShift[] = section.rows.map((r) => r.nurse).filter((n): n is NurseInShift => !!n);
		const stats = jobManagementEnabled
			? calculateJobGroupStats({ nurses: sectionNurses, departmentLocations: departmentLocationsRequired, department })
			: calculateRoleGroupStats({ nurses: sectionNurses, departmentLocations: departmentLocationsRequired, department, hospitalData });
		if (stats.length) currentRow = emitRoleGroupStatRows({ worksheet, currentRow, stats, totalColumns });
	}

	currentRow = emitColumnHeaders({
		worksheet,
		currentRow,
		expandedColumns: layout.expandedColumns,
		printNotes,
		jobManagementEnabled,
		customColumnNames,
	});

	const dataStart = currentRow;
	currentRow = renderStandardSectionRows({
		worksheet,
		rows: section.rows,
		currentRow,
		enabledColumns,
		expandedColumns: layout.expandedColumns,
		hospitalData,
		department,
		timezone,
		jobManagementEnabled,
		printNotes,
		staffPerRow,
		rowGroupingBy,
		noteFlags,
		sortBy,
		customStringOrderMap,
		appendRoleToName,
	});
	applyVerticalMerges({ worksheet, startRow: dataStart, endRow: currentRow - 1, expandedColumns: layout.expandedColumns, rowGroupingBy });

	return currentRow;
};
